import { getApps, initializeApp } from "firebase/app";
import {
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  updateProfile,
  verifyPasswordResetCode,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const requiredConfigKeys = ["apiKey", "authDomain", "projectId"];

export const isFirebaseAuthConfigured = requiredConfigKeys.every(
  (key) => Boolean(firebaseConfig[key]),
);

let firebaseApp;
let firebaseAuth;

const createConfigError = () => {
  const error = new Error("Firebase Auth is not configured");
  error.code = "firebase_not_configured";
  return error;
};

export const getFirebaseAuth = () => {
  if (!isFirebaseAuthConfigured) {
    throw createConfigError();
  }

  if (!firebaseApp) {
    firebaseApp = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
  }

  if (!firebaseAuth) {
    firebaseAuth = getAuth(firebaseApp);
  }

  return firebaseAuth;
};

const googleProvider = new GoogleAuthProvider();
googleProvider.addScope("email");
googleProvider.addScope("profile");
googleProvider.setCustomParameters({ prompt: "select_account" });

export const signInWithGoogle = async () => {
  const auth = getFirebaseAuth();
  const userCredential = await signInWithPopup(auth, googleProvider);
  const idToken = await userCredential.user.getIdToken();
  const credential = GoogleAuthProvider.credentialFromResult(userCredential);

  return {
    idToken,
    accessToken: credential?.accessToken,
    user: userCredential.user,
  };
};

export const registerWithEmailPassword = async ({ name, email, password }) => {
  const auth = getFirebaseAuth();
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);

  if (name) {
    await updateProfile(userCredential.user, { displayName: name });
  }

  await sendEmailVerification(userCredential.user);

  return {
    user: userCredential.user,
  };
};

export const signInWithEmailPassword = async ({ email, password }) => {
  const auth = getFirebaseAuth();
  const userCredential = await signInWithEmailAndPassword(auth, email, password);

  if (!userCredential.user.emailVerified) {
    await sendEmailVerification(userCredential.user);
    await firebaseSignOut(auth);
    const error = new Error("Please verify your email before signing in.");
    error.code = "auth/email-not-verified";
    throw error;
  }

  return {
    idToken: await userCredential.user.getIdToken(true),
    user: userCredential.user,
  };
};

export const sendFirebasePasswordReset = async (email) => {
  await sendPasswordResetEmail(getFirebaseAuth(), email);
};

export const verifyFirebasePasswordResetCode = async (code) => (
  verifyPasswordResetCode(getFirebaseAuth(), code)
);

export const confirmFirebasePasswordReset = async (code, newPassword) => {
  await confirmPasswordReset(getFirebaseAuth(), code, newPassword);
};

export const signOutFromFirebase = async () => {
  if (!isFirebaseAuthConfigured) {
    return;
  }

  try {
    await firebaseSignOut(getFirebaseAuth());
  } catch {
    // Keep Odoo logout independent from Firebase network/session failures.
  }
};
